import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import { type QueryKey, useQuery } from '@tanstack/react-query';
import { rows as toRows, type ID, type Paginatable, type Paginated } from '@hrms/shared';
import { fieldErrors } from '../../../lib/errors';
import { useDebounced } from '../../../lib/hooks';
import { useMut } from '../../../lib/query';
import { isValidTime } from '../../../lib/date';
import {
  Button, ConfirmDialog, DataTable, DateField, Dot, Grid, IconButton, Modal, PageHeader, Row, Select, Stack, Switch, Text, TextField, type Column, type Option,
} from '../../../ui';

export type FieldType = 'text' | 'number' | 'email' | 'select' | 'checkbox' | 'textarea' | 'date' | 'time' | 'color' | 'json' | 'list';

export interface FieldSpec {
  key: string;
  label: string;
  type?: FieldType;
  options?: Option<string | number>[];
  required?: boolean;
  /** full width in the form */
  full?: boolean;
  hint?: string;
  placeholder?: string;
  default?: unknown;
  /** only shown when creating */
  createOnly?: boolean;
}

export type FormValues = Record<string, unknown>;

/** One FieldSpec control bound to a values object. */
export function SpecField({ spec, values, setValue, error }: { spec: FieldSpec; values: FormValues; setValue: (k: string, v: unknown) => void; error?: string }) {
  const v = values[spec.key];
  const t = spec.type ?? 'text';
  const str = v == null ? '' : String(v);
  switch (t) {
    case 'checkbox':
      return (
        <View style={{ paddingTop: 4 }}>
          <Switch label={spec.label} description={error ?? spec.hint} value={!!v} onChange={(c) => setValue(spec.key, c)} />
        </View>
      );
    case 'select':
      return <Select label={spec.label} required={spec.required} error={error} hint={spec.hint} placeholder={spec.placeholder ?? 'Select…'} value={v as string | number | null} options={spec.options ?? []} onChange={(x) => setValue(spec.key, x)} />;
    case 'date':
      return <DateField label={spec.label} required={spec.required} error={error} hint={spec.hint} value={str} clearable onChange={(d) => setValue(spec.key, d || null)} />;
    case 'textarea':
    case 'json':
      return (
        <TextField
          label={spec.label}
          required={spec.required}
          error={error}
          hint={spec.hint}
          placeholder={spec.placeholder}
          multiline
          value={str}
          onChangeText={(x) => setValue(spec.key, x)}
          style={t === 'json' || spec.key === 'body' ? { fontFamily: 'monospace', fontSize: 12, minHeight: t === 'json' ? 120 : 200 } : undefined}
        />
      );
    case 'color':
      return (
        <Row align="flex-end" wrap={false}>
          <View style={{ flex: 1 }}>
            <TextField label={spec.label} required={spec.required} error={error} hint={spec.hint ?? 'Hex colour, e.g. #4f46e5'} autoCapitalize="none" value={str} onChangeText={(x) => setValue(spec.key, x)} />
          </View>
          {/^#[0-9a-f]{6}$/i.test(str) && <View style={{ paddingBottom: spec.hint || error ? 26 : 12 }}><Dot color={str} size={18} /></View>}
        </Row>
      );
    default:
      return (
        <TextField
          label={spec.label}
          required={spec.required}
          error={error}
          hint={spec.hint}
          placeholder={spec.placeholder ?? (t === 'list' ? 'Comma separated' : t === 'time' ? 'HH:mm' : undefined)}
          autoCapitalize={t === 'email' || t === 'time' ? 'none' : undefined}
          keyboardType={t === 'number' ? 'decimal-pad' : t === 'email' ? 'email-address' : t === 'time' ? 'numbers-and-punctuation' : 'default'}
          value={str}
          onChangeText={(x) => setValue(spec.key, t === 'number' ? (x === '' ? null : x.replace(/[^0-9.-]/g, '')) : x)}
        />
      );
  }
}

/** Lays out FieldSpecs: half-width fields in a responsive grid, full-width fields on their own row. */
export function SpecForm({ fields, values, setValue, errors }: { fields: FieldSpec[]; values: FormValues; setValue: (k: string, v: unknown) => void; errors: Record<string, string> }) {
  const groups: FieldSpec[][] = [];
  for (const f of fields) {
    const full = f.full || f.type === 'textarea' || f.type === 'json';
    const last = groups[groups.length - 1];
    if (full) groups.push([f]);
    else if (last && !(last[0].full || last[0].type === 'textarea' || last[0].type === 'json')) last.push(f);
    else groups.push([f]);
  }
  return (
    <Stack gap={14}>
      {groups.map((g) =>
        g.length === 1 && (g[0].full || g[0].type === 'textarea' || g[0].type === 'json') ? (
          <SpecField key={g[0].key} spec={g[0]} values={values} setValue={setValue} error={errors[g[0].key]} />
        ) : (
          <Grid key={g[0].key} min={210} gap={14}>
            {g.map((f) => (
              <SpecField key={f.key} spec={f} values={values} setValue={setValue} error={errors[f.key]} />
            ))}
          </Grid>
        ),
      )}
    </Stack>
  );
}

export function toFormValues(fields: FieldSpec[], m: FormValues | null): FormValues {
  const out: FormValues = {};
  for (const f of fields) {
    const raw = m ? m[f.key] : f.default;
    if (f.type === 'json') out[f.key] = raw == null ? '' : JSON.stringify(raw, null, 2);
    else if (f.type === 'list') out[f.key] = Array.isArray(raw) ? raw.join(', ') : raw ?? '';
    else if (f.type === 'checkbox') out[f.key] = !!raw;
    else out[f.key] = raw ?? (f.type === 'number' || f.type === 'select' ? null : '');
  }
  return out;
}

export function fromFormValues(fields: FieldSpec[], v: FormValues): { data: FormValues; errors?: Record<string, string> } {
  const out: FormValues = {};
  const errors: Record<string, string> = {};
  for (const f of fields) {
    const raw = v[f.key];
    if (f.required && (raw == null || raw === '') && f.type !== 'checkbox') errors[f.key] = `${f.label} is required`;
    if (f.type === 'json') {
      if (raw === '' || raw == null) out[f.key] = null;
      else
        try {
          out[f.key] = JSON.parse(String(raw));
        } catch {
          errors[f.key] = 'Invalid JSON';
        }
    } else if (f.type === 'list') {
      out[f.key] = String(raw ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    } else if (f.type === 'number') {
      out[f.key] = raw === '' || raw == null ? null : Number(raw);
      if (out[f.key] != null && Number.isNaN(out[f.key])) errors[f.key] = 'Enter a number';
    } else if (f.type === 'time') {
      out[f.key] = raw === '' ? null : raw;
      if (raw && !isValidTime(String(raw))) errors[f.key] = 'Use 24h HH:mm';
    } else out[f.key] = raw === '' ? null : raw;
  }
  return Object.keys(errors).length ? { data: out, errors } : { data: out };
}

type Q = Record<string, string | number | boolean | null | undefined>;
export interface CrudResource<M> {
  list: (q?: Q) => Promise<Paginatable<M>>;
  create: (b: Partial<M>) => Promise<M>;
  update: (id: ID, b: Partial<M>) => Promise<M>;
  remove: (id: ID) => Promise<void>;
}

/**
 * Generic master-data CRUD: searchable table + create/edit sheet + delete confirm.
 * `embedded` drops the PageHeader (use inside tabs).
 */
export function CrudList<M extends { id: ID }>({
  title,
  description,
  singular,
  resource,
  queryKey,
  columns,
  fields,
  transformOut,
  renderRowActions,
  headerActions,
  embedded,
  modalWidth,
  canEdit = true,
  onSaved,
  testID,
}: {
  title: string;
  description?: string;
  singular?: string;
  resource: CrudResource<M>;
  queryKey: QueryKey;
  columns: Column<M>[];
  fields: FieldSpec[];
  transformOut?: (v: FormValues, editing: M | null) => FormValues;
  renderRowActions?: (row: M) => React.ReactNode;
  headerActions?: React.ReactNode;
  embedded?: boolean;
  modalWidth?: number;
  canEdit?: boolean;
  onSaved?: (row: M, created: boolean) => void;
  testID?: string;
}) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const dq = useDebounced(search);
  const q = useQuery({ queryKey: [...queryKey, { search: dq, page }], queryFn: () => resource.list({ search: dq || undefined, page }) });
  const [editing, setEditing] = useState<M | null>(null);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleting, setDeleting] = useState<M | null>(null);
  const name = (singular ?? title.replace(/s$/, '')).toLowerCase();
  const visible = fields.filter((f) => !(f.createOnly && editing));

  const save = useMut((data: FormValues) => (editing ? resource.update(editing.id, data as Partial<M>) : resource.create(data as Partial<M>)), {
    invalidate: [queryKey],
    success: editing ? `${singular ?? title} updated` : `${singular ?? title} created`,
    onSuccess: (r) => {
      setOpen(false);
      onSaved?.(r, !editing);
    },
    onError: (e) => setErrors(fieldErrors(e)),
  });
  const del = useMut((id: ID) => resource.remove(id), { invalidate: [queryKey], success: `${singular ?? title} deleted`, onSuccess: () => setDeleting(null) });

  const openForm = (m: M | null) => {
    setEditing(m);
    setValues(toFormValues(fields, m as unknown as FormValues | null));
    setErrors({});
    setOpen(true);
  };
  const submit = () => {
    const { data, errors: errs } = fromFormValues(visible, values);
    if (errs) return setErrors(errs);
    save.mutate(transformOut ? transformOut(data, editing) : data);
  };

  const allCols = useMemo<Column<M>[]>(
    () => [
      ...columns,
      ...(canEdit || renderRowActions
        ? [
            {
              key: '_actions',
              title: '',
              sortable: false,
              width: canEdit && renderRowActions ? 190 : 96,
              flex: 0,
              align: 'right' as const,
              render: (r: M) => (
                <Row gap={2} wrap={false}>
                  {renderRowActions?.(r)}
                  {canEdit && (
                    <>
                      <IconButton icon="edit" size="sm" label={`Edit ${name}`} onPress={() => openForm(r)} />
                      <IconButton icon="trash" size="sm" label={`Delete ${name}`} onPress={() => setDeleting(r)} />
                    </>
                  )}
                </Row>
              ),
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, canEdit, renderRowActions],
  );

  const data = q.data;
  const tableRows: M[] | Paginated<M> | undefined = data && !Array.isArray(data) ? data : toRows(data);
  const addBtn = canEdit ? <Button variant="primary" icon="plus" size={embedded ? 'sm' : 'md'} title={`New ${name}`} onPress={() => openForm(null)} /> : null;
  return (
    <Stack gap={12} testID={testID}>
      {!embedded && (
        <PageHeader
          title={title}
          subtitle={description}
          actions={
            headerActions || addBtn ? (
              <>
                {headerActions}
                {addBtn}
              </>
            ) : undefined
          }
        />
      )}
      {embedded && description ? <Text variant="muted">{description}</Text> : null}
      <Row gap={8}>
        <TextField containerStyle={{ flex: 1, minWidth: 200, maxWidth: 360 }} placeholder={`Search ${title.toLowerCase()}…`} accessibilityLabel={`Search ${title.toLowerCase()}`} value={search} onChangeText={(s) => { setSearch(s); setPage(1); }} />
        {embedded && headerActions}
        {embedded && addBtn}
      </Row>
      <DataTable<M> columns={allCols} rows={tableRows} loading={q.isLoading} onPageChange={setPage} onRowPress={canEdit ? (r) => openForm(r) : undefined} emptyTitle={`No ${title.toLowerCase()} yet`} />
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? `Edit ${name}` : `New ${name}`}
        width={modalWidth ?? 620}
        footer={
          <>
            <Button title="Cancel" onPress={() => setOpen(false)} />
            <Button title="Save" variant="primary" loading={save.isPending} onPress={submit} testID="crud-save" />
          </>
        }
      >
        <SpecForm fields={visible} values={values} errors={errors} setValue={(k, v) => setValues((s) => ({ ...s, [k]: v }))} />
      </Modal>
      <ConfirmDialog open={!!deleting} onClose={() => setDeleting(null)} onConfirm={() => deleting && del.mutate(deleting.id)} title={`Delete ${name}?`} message="This cannot be undone." confirmLabel="Delete" danger loading={del.isPending} />
    </Stack>
  );
}
